import { useLocale, useTranslation } from "@/lib/i18n";
import { localizedPath } from "@/lib/i18n/slug-map";
import { Link } from "@tanstack/react-router";
import { openCookieSettings } from "@/lib/cookie-consent-actions";

const to = {
  annonser: "/annonser",
  skapa: "/skapa",
  priser: "/priser",
  tips: "/tips",
  guider: "/guider",
  jamfor: "/jamfor",
  regler: "/regler",
  videoskanning: "/videoskanning",
  saFungerarDet: "/sa-fungerar-det",
  om: "/om",
  varforKostarDet: "/varfor-kostar-det",
  support: "/support",
  villkor: "/anvandarvillkor",
  integritet: "/integritetspolicy",
  cookies: "/cookiepolicy",
} as const;

export function SiteFooter() {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const local = (path: string) => localizedPath(path, lang);

  const columns = [
    {
      title: "Sellavo",
      links: [
        { label: t("footer.om"), to: local(to.om) },
        { label: t("footer.howItWorks"), to: local(to.saFungerarDet) },
        { label: t("footer.whyItCosts"), to: local(to.varforKostarDet) },
        { label: t("footer.pricing"), to: local(to.priser) },
        { label: t("footer.tips"), to: local(to.tips) },
      ],
    },
    {
      title: t("footer.colTrade"),
      links: [
        { label: t("footer.buyAndSellLocally"), to: local(to.annonser) },
        { label: t("nav.createAd"), to: local(to.skapa) },
        { label: t("footer.guides"), to: local(to.guider) },
        { label: t("footer.compare"), to: local(to.jamfor) },
        { label: t("footer.rules"), to: local(to.regler) },
        { label: t("footer.videoScan"), to: local(to.videoskanning) },
      ],
    },
    {
      title: t("footer.colLegal"),
      links: [
        { label: t("footer.help"), to: local(to.support) },
        { label: t("footer.terms"), to: local(to.villkor) },
        { label: t("footer.privacy"), to: local(to.integritet) },
        { label: t("footer.cookies"), to: local(to.cookies) },
        { label: t("footer.cookieSettings"), onClick: openCookieSettings },
      ],
    },
  ];

  return (
    <footer className="border-t border-border/80 bg-background">
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-3">
          {columns.map((column) => (
            <div key={column.title}>
              <p className="text-sm font-semibold text-foreground">{column.title}</p>
              <ul className="mt-3 space-y-2">
                {column.links.map((link) => (
                  <li key={link.label}>
                    {"to" in link && link.to ? (
                      <Link
                        to={link.to}
                        className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <button
                        type="button"
                        onClick={(link as { onClick?: () => void }).onClick}
                        className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {link.label}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-8 text-xs text-muted-foreground">
          {t("footer.tagline")} · © {new Date().getFullYear()} Sellavo
        </p>
      </div>
    </footer>
  );
}
